/* eslint-disable */
import React, { useMemo, useState, useEffect } from "react";
import {
  Card,
  Group,
  Text,
  Select,
  TextInput,
  Table,
  Avatar,
  Badge,
  Center,
  Collapse,
  ActionIcon,
  Box,
  Modal,
  Stack,
  SimpleGrid,
  Divider,
} from "@mantine/core";
import { DateRangePicker } from "@mantine/dates";
import {
  IconChevronDown,
  IconSearch,
  IconList,
  IconEdit,
  IconTrash,
} from "@tabler/icons-react";
import { PermissionActionIcon } from "api/permission/permissionActionBtn";
import BalanceRowEditModal from "../BalanceRowEditModal";

import { useGetDepartments } from "api/department/getAllDepartment";
import {
  LeaveBalanceQueryParams,
  useGetLeaveBalanceSummary,
  UserLeaveBalance,
} from "api/newLeave/leaveHistory";
import { useUser } from "hooks/useUser";
import { textOverlap } from "utils/commonFunction";

const safeNum = (v: any) => {
  if (typeof v === "number") {
    return Number(v).toFixed(2);
  }
  if (typeof v === "string" && !isNaN(Number(v))) {
    return Number(v).toFixed(2);
  }
  return v ?? "-";
};

const formatDate = (iso?: string | null) => {
  if (!iso || iso=='-') return "-";
  try {
    const d = iso.length === 10 ? new Date(`${iso}T00:00:00Z`) : new Date(iso);
    return d.toLocaleDateString();
  } catch (e) {
    return iso;
  }
};

const iso = (d: Date | null) => (d ? d.toISOString().slice(0, 10) : undefined);

const getApproverNames = (approvals: any): string => {
  if (!approvals || !Array.isArray(approvals) || approvals.length === 0)
    return "-";

  const names = approvals
    .map((a: any) => {
      if (!a && a !== 0) return null;
      if (typeof a === "string") return a;
      if (a.name) return a.name;
      if (a.approvedBy) return a.approvedBy;

      if (a.approver && typeof a.approver === "object") {
        const fn =
          a.approver.firstName || a.approver.first_name || a.approver.name;
        const ln = a.approver.lastName || a.approver.last_name;
        const full = [fn, ln].filter(Boolean).join(" ");
        return full || JSON.stringify(a.approver);
      }

      if (a.user && typeof a.user === "object") {
        const fn = a.user.firstName || a.user.first_name || a.user.name;
        const ln = a.user.lastName || a.user.last_name;
        const full = [fn, ln].filter(Boolean).join(" ");
        return full || JSON.stringify(a.user);
      }

      if (a.firstName || a.lastName)
        return [a.firstName, a.lastName].filter(Boolean).join(" ");

      if (a.fullName || a.full_name) return a.fullName || a.full_name;

      try {
        return JSON.stringify(a);
      } catch (e) {
        return null;
      }
    })
    .filter(Boolean);

  return names.length > 0 ? names.join(", ") : "-";
};

const humanizeKey = (k = "") => {
  const map: Record<string, string> = {
    annualLeave: "Annual Leave",
    sickLeave: "Sick Leave",
    pressingNecessity: "Pressing Necessity",
    compassionate: "Compassionate",
    bereavement: "Bereavement",
    juryDuty: "Jury Duty",
    election: "Election",
    bandBusiness: "Band Business",
    governance: "Governance",
    education: "Education",
    professionalDevelopment: "Professional Development",
    culturalSpiritual: "Cultural / Spiritual",
  };
  if (map[k]) return map[k];

  return k
    .replace(/([A-Z])/g, " $1")
    .replace(/_/g, " ")
    .replace(/^./, (s) => s.toUpperCase());
};

interface LeaveSummaryProps {
  onDataUpdate?: (data: UserLeaveBalance[]) => void;
}

export default function LeaveSummary({ onDataUpdate }: LeaveSummaryProps) {
  const { user } = useUser();
  const ORG_ID = user?.org?._id;
  const { data: deptData, isLoading: isLoadingDepartments } =
    useGetDepartments();

  const [departmentName, setDepartmentName] = useState<string | null>(null);
  const [userName, setUserName] = useState("");
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([
    null,
    null,
  ]);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const [breakdownOpen, setBreakdownOpen] = useState(false);
  const [selectedBreakdown, setSelectedBreakdown] = useState<any>(null);
  const [selectedUserName, setSelectedUserName] = useState<string>("");

  const roleNormalized = (user?.role ?? "").toString();
  const isAdmin = /admin/i.test(roleNormalized);
  const isSupervisor = /supervisor/i.test(roleNormalized);
  const isMember = /member/i.test(roleNormalized);
  const disabledDept = isSupervisor || isMember;

  const deptArray: any[] = deptData?.data ?? [];
  const deptOptions = useMemo(() => {
    if (!Array.isArray(deptArray)) return [];
    const baseOpts = deptArray.map((d) => ({
      value: d.name,
      label: d.name,
    }));

    if (disabledDept && user?.department?.name) {
      const myName = user.department.name;
      const exists = baseOpts.some((o) => o.value === myName);
      if (!exists) {
        baseOpts.unshift({
          value: myName,
          label: user.department.name ?? "My Department",
        });
      }
    }
    return baseOpts;
  }, [deptArray, disabledDept, user]);

  useEffect(() => {
    if (!user) return;
    if (disabledDept && user.department?.name) {
      setDepartmentName(user.department.name);
    } else if (isAdmin) {
      setDepartmentName(null);
    }
  }, [user, disabledDept, isAdmin]);

  const params: LeaveBalanceQueryParams = {
    userName: userName?.trim() || undefined,
    departmentName: departmentName || undefined,
    startDateFrom: iso(dateRange[0]) || undefined,
    startDateTo: iso(dateRange[1]) || undefined,
  };

  const resp = useGetLeaveBalanceSummary(ORG_ID ?? "", params, Boolean(ORG_ID));
  // Memoize so the `?? []` fallback doesn't produce a new array reference every
  // render (which fed the onDataUpdate effect and caused an infinite re-render
  // loop — "Maximum update depth exceeded").
  const rowsList: UserLeaveBalance[] = useMemo(
    () => resp?.data?.data ?? [],
    [resp?.data]
  );
  // Row Edit (set) / Delete (zero-out) a leave-type balance bucket.
  const [balanceModal, setBalanceModal] = useState<{
    userId: string;
    name: string;
    mode: "edit" | "zero";
  } | null>(null);

  useEffect(() => {
    onDataUpdate?.(rowsList);
  }, [rowsList, onDataUpdate]);

  const openBreakdown = (item: UserLeaveBalance) => {
    setSelectedBreakdown(item.allocatedBreakdown ?? {});
    setSelectedUserName(
      `${item.firstName ?? ""} ${item.lastName ?? ""}`.trim(),
    );
    setBreakdownOpen(true);
  };

  return (
    <Card shadow="sm" radius="md" p="md">
      {balanceModal && (
        <BalanceRowEditModal
          opened={!!balanceModal}
          onClose={() => setBalanceModal(null)}
          mode={balanceModal.mode}
          userId={balanceModal.userId}
          employeeName={balanceModal.name}
          onDone={() => resp.refetch?.()}
        />
      )}
      <Group position="apart">
        <div>
          <Text fw={700} fz={16}>
            Leave Balance Summary
          </Text>
          <Text size="xs" color="dimmed">
            Filters:{" "}
            {dateRange[0] && dateRange[1]
              ? `${formatDate(iso(dateRange[0])!)} - ${formatDate(
                  iso(dateRange[1])!,
                )}`
              : "None"}
          </Text>
        </div>

        <Group grow spacing="sm" align="end">
          <Box style={{ width: "100%" }}>
            <Text size="sm" fw={500} mb={4}>
              Date Range
            </Text>
            <DateRangePicker
              placeholder="Select date range"
              value={dateRange}
              onChange={setDateRange}
              style={{ width: "100%" }}
            />
          </Box>

          <Box style={{ width: "100%" }}>
            <Text size="sm" fw={500} mb={4}>
              Department
            </Text>
            <Select
              placeholder="Select department"
              data={deptOptions}
              value={departmentName}
              onChange={(v) => {
                if (!disabledDept) setDepartmentName(v);
              }}
              clearable={!disabledDept}
              // searchable
              style={{ width: "100%" }}
              nothingFound="No departments found"
              disabled={disabledDept || isLoadingDepartments}
            />
          </Box>

          <Box style={{ width: "100%" }}>
            <Text size="sm" fw={500} mb={4}>
              Employee
            </Text>
            <TextInput
              placeholder="Search employee"
              icon={<IconSearch size={14} />}
              value={userName}
              onChange={(e) => setUserName(e.currentTarget.value)}
              style={{ width: "100%" }}
            />
          </Box>
        </Group>
      </Group>

      <Table
        verticalSpacing="sm"
        mt="md"
        withBorder
        style={{ background: "#ffffff" }}
      >
        <thead style={{ background: "#f1f3f5" }}>
          <tr>
            <th>Employee</th>
            <th>Department</th>
            <th>Total Allocated</th>
            <th>Used</th>
            <th>Pending</th>
            <th>Balance</th>
            <th>Leave Without Pay</th>
            <th>Carry Forward</th>
            <th>Action</th>
            <th />
          </tr>
        </thead>

        <tbody style={{ background: "#ffffff" }}>
          {!ORG_ID ? (
            <tr>
              <td colSpan={9}>
                <Center>Waiting for organization data…</Center>
              </td>
            </tr>
          ) : resp.isLoading ? (
            <tr>
              <td colSpan={9}>
                <Center>Loading...</Center>
              </td>
            </tr>
          ) : rowsList.length === 0 ? (
            <tr>
              <td colSpan={9}>
                <Center>No records found</Center>
              </td>
            </tr>
          ) : (
            rowsList.map((item) => {
              const id = item.userId ?? `${item.firstName}-${item.lastName}`;
              const avatarLetter =
                (item.firstName && item.firstName[0]) ||
                (item.lastName && item.lastName[0]) ||
                "U";

              return (
                <React.Fragment key={id}>
                  <tr>
                    <td style={{ fontSize: "12px" }}>
                      <Group>
                        <Avatar size={24} color="blue" radius="xl">
                          {avatarLetter.toUpperCase()}
                        </Avatar>
                        <div>
                          <Text fw={600} style={{ fontSize: "12px" }}>
                            {textOverlap(
                              item?.firstName + " " + item?.lastName,
                              20,
                            )}
                          </Text>
                          <Text
                            size="xs"
                            color="dimmed"
                            style={{ fontSize: "12px" }}
                          >
                            {item.startDate ? formatDate(item.startDate) : ""}
                          </Text>
                        </div>
                      </Group>
                    </td>

                    <td style={{ fontSize: "12px" }}>
                      {item.department ?? "-"}
                    </td>
                    <td style={{ fontSize: "12px" }}>
                      {safeNum(item.totalAllocated)}
                    </td>
                    <td style={{ fontSize: "12px" }}>
                      {safeNum(item.totalUsed)}
                    </td>
                    <td style={{ fontSize: "12px" }}>
                      {safeNum(item.totalPending)}
                    </td>
                    <td style={{ fontSize: "12px" }}>
                      {safeNum(item.totalBalance)}
                    </td>
                    <td style={{ fontSize: "12px" }}>
                      {safeNum(item.leaveWithoutPayUsed ?? "-")}
                    </td>
                    <td style={{ fontSize: "12px" }}>
                      {typeof item.carryForward === "string"
                        ? item.carryForward
                        : (item.carryForward ?? "-")}
                    </td>

                    <td style={{ fontSize: "12px" }}>
                      <Group spacing={4}>
                        <ActionIcon
                          onClick={() =>
                            setExpanded((s) => ({ ...s, [id]: !s[id] }))
                          }
                        >
                          <IconChevronDown size={18} />
                        </ActionIcon>

                        <ActionIcon
                          title="View allocated breakdown"
                          onClick={() => openBreakdown(item)}
                        >
                          <IconList size={16} />
                        </ActionIcon>

                        <PermissionActionIcon
                          permission="employees.userBalance"
                          aria-label="Edit balance"
                          color="blue"
                          onClick={() =>
                            setBalanceModal({
                              userId: item.userId,
                              name: `${item.firstName ?? ""} ${item.lastName ?? ""}`.trim(),
                              mode: "edit",
                            })
                          }
                        >
                          <IconEdit size={16} />
                        </PermissionActionIcon>

                        <PermissionActionIcon
                          permission="employees.userBalance"
                          aria-label="Clear balance"
                          color="red"
                          onClick={() =>
                            setBalanceModal({
                              userId: item.userId,
                              name: `${item.firstName ?? ""} ${item.lastName ?? ""}`.trim(),
                              mode: "zero",
                            })
                          }
                        >
                          <IconTrash size={16} />
                        </PermissionActionIcon>
                      </Group>
                    </td>
                  </tr>

                  <tr>
                    <td colSpan={9} style={{ padding: 0 }}>
                      <Collapse in={!!expanded[id]} transitionDuration={200}>
                        <div
                          style={{
                            background: "#f8f9fa",
                            padding: 15,
                            marginTop: -10,
                          }}
                        >
                          <Text
                            fw={600}
                            mt="md"
                            mb={6}
                            style={{ fontSize: "12px" }}
                          >
                            Leave History
                          </Text>

                          <Table
                            withBorder
                            style={{ background: "#ffffff", fontSize: "12px" }}
                          >
                            <thead
                              style={{
                                background: "#f1f3f5",
                                fontSize: "12px",
                              }}
                            >
                              <tr>
                                <th style={{ fontSize: "12px" }}>Type</th>
                                <th style={{ fontSize: "12px" }}>From</th>
                                <th style={{ fontSize: "12px" }}>To</th>
                                <th style={{ fontSize: "12px" }}>Days</th>
                                <th style={{ fontSize: "12px" }}>
                                  Deduct Leave
                                </th>
                                <th style={{ fontSize: "12px" }}>
                                  Late Minutes
                                </th>
                                <th style={{ fontSize: "12px" }}>Status</th>
                                <th style={{ fontSize: "12px" }}>
                                  Approved By
                                </th>
                              </tr>
                            </thead>
                            <tbody
                              style={{
                                background: "#ffffff",
                                fontSize: "12px",
                              }}
                            >
                              {item.leaveHistory &&
                              item.leaveHistory.length > 0 ? (
                                item.leaveHistory.map((h: any, i: number) => (
                                  <tr key={`${id}-hist-${i}`}>
                                    <td style={{ fontSize: "12px" }}>
                                      {h.leaveType ?? "-"}
                                    </td>
                                    <td style={{ fontSize: "12px" }}>
                                      {formatDate(h.fromDate ?? h.from)}
                                    </td>
                                    <td style={{ fontSize: "12px" }}>
                                      {formatDate(h.toDate ?? h.to)}
                                    </td>
                                    <td style={{ fontSize: "12px" }}>
                                      {safeNum(h.numberOfDays)}
                                    </td>
                                    <td style={{ fontSize: "12px" }}>
                                      {h.deductLeave !== undefined
                                        ? safeNum(h.deductLeave)
                                        : "-"}
                                    </td>
                                    <td style={{ fontSize: "12px" }}>
                                      {h.lateMinutes !== undefined
                                        ? String(h.lateMinutes)
                                        : "-"}
                                    </td>
                                    <td style={{ fontSize: "12px" }}>
                                      <Badge
                                        color={
                                          (h.status || "").toLowerCase() ===
                                          "approved"
                                            ? "green"
                                            : (h.status || "").toLowerCase() ===
                                                "pending"
                                              ? "yellow"
                                              : "red"
                                        }
                                        style={{ fontSize: "12px" }}
                                      >
                                        {h.status ?? "-"}
                                      </Badge>
                                    </td>
                                    <td style={{ fontSize: "12px" }}>
                                      {getApproverNames(h.approvals)}
                                    </td>
                                  </tr>
                                ))
                              ) : (
                                <tr>
                                  <td colSpan={9} style={{ fontSize: "12px" }}>
                                    No history
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </Table>
                        </div>
                      </Collapse>
                    </td>
                  </tr>
                </React.Fragment>
              );
            })
          )}
        </tbody>
      </Table>

      <Modal
        opened={breakdownOpen}
        onClose={() => setBreakdownOpen(false)}
        title={`${selectedUserName || "Employee"} — Allocated Breakdown`}
        size="lg"
        centered
      >
        <Stack spacing="sm">
          <Group position="apart" align="center">
            <div>
              <Text fw={600}>{selectedUserName || "-"}</Text>
              <Text size="xs" color="dimmed">
                Totals — Allocated:{" "}
                {selectedBreakdown
                  ? safeNum(selectedBreakdown.totalAllocated ?? "")
                  : safeNum(0)}{" "}
                &nbsp; Used:{" "}
                {selectedBreakdown
                  ? safeNum(selectedBreakdown.totalUsed ?? "")
                  : safeNum(0)}
              </Text>
            </div>
          </Group>

          <Divider />

          {selectedBreakdown && Object.keys(selectedBreakdown).length > 0 ? (
            <SimpleGrid cols={2} breakpoints={[{ maxWidth: "sm", cols: 1 }]}>
              {Object.entries(selectedBreakdown).map(([k, v]) => {
                if (k === "totalAllocated" || k === "totalUsed") return null;
                return (
                  <Box
                    key={k}
                    style={{
                      padding: 10,
                      borderRadius: 8,
                      background: "#f8f9fa",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Text size="sm">{humanizeKey(k)}</Text>
                    <Text size="sm" fw={600}>
                      {safeNum(v)}
                    </Text>
                  </Box>
                );
              })}
            </SimpleGrid>
          ) : (
            <Center style={{ padding: 12 }}>
              <Text color="dimmed">No allocated breakdown available</Text>
            </Center>
          )}
        </Stack>
      </Modal>
    </Card>
  );
}
